import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { X, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Active filter chips row with clear all and copy link actions
 * Shows scope chips (collection/category) with different styling
 */
export const ActiveChipsBar = ({ chips = [], onRemoveChip, onClearAll }) => {
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Filter link copied to clipboard');
    } catch {
      toast.error('Unable to copy automatically. Copy the address from your browser.');
    }
  };

  if (chips.length === 0) return null;

  const scopeChips = chips.filter(c => c.type === 'scope');
  const otherChips = chips.filter(c => c.type !== 'scope');

  return (
    <div 
      className="border-b bg-muted/20"
      data-testid="active-filters-chip-list"
    >
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
            ACTIVE:
          </span>
          
          {/* Scope chips (stronger style, not all removable) */}
          {scopeChips.map((chip) => (
            <Badge 
              key={`${chip.key}-${chip.value}`}
              variant="default"
              className="gap-1.5 min-w-0 max-w-full whitespace-normal bg-primary text-primary-foreground"
              data-testid={`active-filter-chip-scope-${chip.key}-${chip.value}`}
            >
              <span className="min-w-0 break-words [overflow-wrap:anywhere]">{chip.label}</span>
              {chip.removable && (
                <button
                  onClick={() => onRemoveChip(chip)}
                  className="ml-1 shrink-0 hover:bg-primary-foreground/20 rounded-full p-0.5 transition-colors"
                  data-testid={`active-filter-chip-remove-button-${chip.key}`}
                  aria-label={`Remove ${chip.label}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}

          {/* Filter chips */}
          {otherChips.map((chip) => (
            <Badge 
              key={`${chip.key}-${chip.value}`}
              variant="secondary"
              className="gap-1.5 min-w-0 max-w-full whitespace-normal"
              data-testid={`active-filter-chip-${chip.key}-${chip.value}`}
            >
              <span className="min-w-0 break-words [overflow-wrap:anywhere]">{chip.label}</span>
              {chip.removable && (
                <button
                  onClick={() => onRemoveChip(chip)}
                  className="ml-1 shrink-0 hover:bg-muted rounded-full p-0.5 transition-colors"
                  data-testid={`active-filter-chip-remove-button-${chip.key}-${chip.value}`}
                  aria-label={`Remove ${chip.label}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}

          {/* Actions */}
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={copyLink}
              className="h-7 text-xs"
              data-testid="active-filters-copy-link-button"
            >
              <Copy className="w-3 h-3 mr-1" />
              Copy link
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearAll}
              className="h-7 text-xs"
              data-testid="active-filters-clear-all-button"
            >
              Clear all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
